import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import Avatar from '../components/Avatar';

const ChevronLeft = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);
const SendIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
  </svg>
);

function formatTs(ts) {
  const d = new Date(ts);
  const now = new Date();
  const diff = now - d;
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatMsgTime(ts) {
  return new Date(ts).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

// ── Conversation List ─────────────────────────────────────────────────────────
export function MessagesList() {
  const navigate = useNavigate();
  const { getMyConversations, getUserById, getRideById, currentUser } = useApp();
  const convs = getMyConversations();

  return (
    <div className="page">
      <div style={{ paddingTop: 8, marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800 }}>Messages</h1>
        <p style={{ fontSize: 13, color: 'var(--gray-400)', marginTop: 2 }}>
          {convs.length} conversation{convs.length !== 1 ? 's' : ''}
        </p>
      </div>

      {convs.length === 0 ? (
        <div style={{
          background: 'var(--navy-light)', border: '1px solid var(--navy-border)',
          borderRadius: 'var(--radius)', padding: '40px 24px', textAlign: 'center',
        }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>💬</div>
          <p style={{ fontWeight: 600, marginBottom: 8 }}>No messages yet</p>
          <p style={{ color: 'var(--gray-400)', fontSize: 13, marginBottom: 16 }}>
            Messages appear when a driver accepts your ride request.
          </p>
          <button className="btn btn-primary" onClick={() => navigate('/browse')}>
            Find a Ride
          </button>
        </div>
      ) : (
        convs.map(conv => {
          const otherId = conv.participants.find(p => p !== currentUser.id);
          const other = getUserById(otherId);
          const ride = getRideById(conv.rideId);
          const last = conv.messages[conv.messages.length - 1];

          return (
            <button
              key={conv.id}
              onClick={() => navigate(`/messages/${conv.id}`)}
              style={{
                width: '100%', background: 'var(--navy-light)',
                border: '1px solid var(--navy-border)',
                borderRadius: 'var(--radius)', padding: '14px 16px',
                display: 'flex', alignItems: 'center', gap: 12,
                marginBottom: 10, textAlign: 'left',
                transition: 'border-color 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--navy-border)'}
            >
              <Avatar user={other} size={46} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 3 }}>
                  <span style={{ fontSize: 15, fontWeight: 700 }}>{other?.name || 'Unknown'}</span>
                  <span style={{ fontSize: 11, color: 'var(--gray-500)', flexShrink: 0 }}>{formatTs(last?.timestamp)}</span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 2 }}>
                  {ride ? `${ride.departure} → ${ride.destination}` : 'Ride'}
                </div>
                <div style={{ fontSize: 13, color: 'var(--gray-400)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {last?.senderId === 'system'
                    ? <em>{last.text}</em>
                    : last?.senderId === currentUser.id
                    ? `You: ${last.text}`
                    : last?.text
                  }
                </div>
              </div>
            </button>
          );
        })
      )}
    </div>
  );
}

// ── Chat Window ───────────────────────────────────────────────────────────────
export function ChatWindow() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { conversations, currentUser, getUserById, getRideById, sendMessage } = useApp();
  const [text, setText] = useState('');
  const bottomRef = useRef();

  const conv = conversations.find(c => c.id === id);
  const otherId = conv?.participants.find(p => p !== currentUser.id);
  const other = getUserById(otherId);
  const ride = conv ? getRideById(conv.rideId) : null;

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conv?.messages]);

  if (!conv) {
    return (
      <div className="page" style={{ textAlign: 'center', paddingTop: 80 }}>
        <p style={{ color: 'var(--gray-400)' }}>Conversation not found.</p>
        <button className="btn btn-ghost" onClick={() => navigate('/messages')} style={{ marginTop: 16 }}>
          Back to Messages
        </button>
      </div>
    );
  }

  const handleSend = () => {
    if (!text.trim()) return;
    sendMessage(id, text.trim());
    setText('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Group messages by date
  const grouped = [];
  let lastDate = null;
  conv.messages.forEach(msg => {
    const d = new Date(msg.timestamp).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
    if (d !== lastDate) { grouped.push({ type: 'date', label: d }); lastDate = d; }
    grouped.push({ type: 'msg', msg });
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: 'var(--navy)' }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px', borderBottom: '1px solid var(--navy-border)',
        background: 'var(--navy-light)', display: 'flex', alignItems: 'center', gap: 12,
        flexShrink: 0,
      }}>
        <button onClick={() => navigate('/messages')} style={{ background: 'none', color: 'var(--gray-300)', padding: 4, marginRight: 4 }}>
          <ChevronLeft />
        </button>
        <button onClick={() => navigate(`/profile/${other?.id}`)} style={{ background: 'none', padding: 0 }}>
          <Avatar user={other} size={38} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 1 }}>{other?.name}</div>
          {ride && (
            <div style={{ fontSize: 11, color: 'var(--gray-500)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {ride.departure} → {ride.destination}
            </div>
          )}
        </div>
        {ride && (
          <button
            onClick={() => navigate(`/ride/${ride.id}`)}
            style={{ background: 'var(--navy-mid)', border: '1px solid var(--navy-border)', borderRadius: 6, padding: '5px 10px', fontSize: 11, color: 'var(--gray-300)' }}
          >
            View Ride
          </button>
        )}
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: 0 }}>
        {grouped.map((item, i) => {
          if (item.type === 'date') {
            return (
              <div key={i} style={{ textAlign: 'center', margin: '12px 0', fontSize: 11, color: 'var(--gray-500)' }}>
                {item.label}
              </div>
            );
          }
          const { msg } = item;
          const isSystem = msg.senderId === 'system';
          const isMine = msg.senderId === currentUser.id;

          if (isSystem) {
            return (
              <div key={msg.id} style={{
                textAlign: 'center', margin: '8px 0',
                padding: '8px 16px', background: 'var(--navy-mid)',
                borderRadius: 12, fontSize: 12, color: 'var(--gray-400)',
                maxWidth: 280, alignSelf: 'center',
              }}>
                {msg.text}
              </div>
            );
          }

          return (
            <div key={msg.id} style={{
              display: 'flex', flexDirection: 'column',
              alignItems: isMine ? 'flex-end' : 'flex-start',
              marginBottom: 6,
            }}>
              <div style={{
                maxWidth: '76%',
                background: isMine ? 'var(--accent)' : 'var(--navy-light)',
                border: isMine ? 'none' : '1px solid var(--navy-border)',
                borderRadius: isMine ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                padding: '10px 14px',
                fontSize: 14, lineHeight: 1.5,
                color: isMine ? 'white' : 'var(--white)',
              }}>
                {msg.text}
              </div>
              <span style={{ fontSize: 10, color: 'var(--gray-600)', marginTop: 3, paddingLeft: 4, paddingRight: 4 }}>
                {formatMsgTime(msg.timestamp)}
              </span>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div style={{
        padding: '12px 16px',
        paddingBottom: 'calc(12px + env(safe-area-inset-bottom))',
        borderTop: '1px solid var(--navy-border)',
        background: 'var(--navy-light)',
        display: 'flex', gap: 10, alignItems: 'flex-end', flexShrink: 0,
      }}>
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a message..."
          rows={1}
          style={{
            flex: 1, background: 'var(--navy-mid)',
            border: '1.5px solid var(--navy-border)',
            borderRadius: 20, color: 'var(--white)',
            padding: '10px 16px', fontSize: 14,
            outline: 'none', resize: 'none',
            maxHeight: 100, lineHeight: 1.5,
            fontFamily: 'inherit',
          }}
          onFocus={e => e.target.style.borderColor = 'var(--accent)'}
          onBlur={e => e.target.style.borderColor = 'var(--navy-border)'}
          onInput={e => {
            e.target.style.height = 'auto';
            e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px';
          }}
        />
        <button
          onClick={handleSend}
          disabled={!text.trim()}
          style={{
            width: 42, height: 42, borderRadius: '50%',
            background: text.trim() ? 'var(--accent)' : 'var(--navy-border)',
            color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, transition: 'background 0.2s',
            border: 'none',
          }}
        >
          <SendIcon />
        </button>
      </div>
    </div>
  );
}
